/**
 * Document Validation Utilities
 *
 * Pure validation functions for international document types.
 * Each function receives a raw string value and a translator, and returns
 * { valid: boolean, error: string | null }.
 *
 * Supported documents:
 * - CPF (Brazil) — Mod-11
 * - RUT (Chile) — Mod-11 with K
 * - DNI (Argentina) — Length check
 * - CI (Bolivia) — Length check
 * - SSN (United States) — Format + area rules
 * - NIF (Portugal) — Mod-11
 * - NIF/NIE (Espanha) — letra mod-23
 * - CURP (Mexico) — Regex format
 * - DPI (Guatemala) — Length check
 * - Cédula (Paraguay) — Length check
 * - CNPJ (Brazil) — Mod-11 with two check digits
 */

export type Translator = (key: string) => string

export interface ValidationResult {
  valid: boolean
  error: string | null
}

export type DocumentValidator = (value: string | null | undefined, t: Translator) => ValidationResult

export const validateCPF: DocumentValidator = (value, t) => {
  const digits = (value || '').replace(/\D/g, '')

  if (digits.length !== 11) {
    return { valid: false, error: t('CPF deve conter 11 dígitos') }
  }

  if (/^(\d)\1+$/.test(digits)) {
    return { valid: false, error: t('CPF inválido') }
  }

  let sum = 0
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i]) * (10 - i)
  }
  let remainder = (sum * 10) % 11
  if (remainder === 10) remainder = 0
  if (remainder !== parseInt(digits[9])) {
    return { valid: false, error: t('CPF inválido') }
  }

  sum = 0
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i]) * (11 - i)
  }
  remainder = (sum * 10) % 11
  if (remainder === 10) remainder = 0
  if (remainder !== parseInt(digits[10])) {
    return { valid: false, error: t('CPF inválido') }
  }

  return { valid: true, error: null }
}

export const validateRUT: DocumentValidator = (value, t) => {
  const cleaned = (value || '').replace(/[^0-9Kk]/g, '')

  if (cleaned.length < 8 || cleaned.length > 9) {
    return { valid: false, error: t('RUT deve conter 8 ou 9 caracteres') }
  }

  const body = cleaned.slice(0, -1)
  const checkDigit = cleaned.slice(-1).toUpperCase()

  let sum = 0
  let multiplier = 2

  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * multiplier
    multiplier = multiplier === 7 ? 2 : multiplier + 1
  }

  const remainder = sum % 11
  let expectedCheckDigit: string

  if (remainder === 0) {
    expectedCheckDigit = '0'
  } else if (remainder === 1) {
    expectedCheckDigit = 'K'
  } else {
    expectedCheckDigit = (11 - remainder).toString()
  }

  if (checkDigit !== expectedCheckDigit) {
    return { valid: false, error: t('RUT inválido') }
  }

  return { valid: true, error: null }
}

export const validateDNI: DocumentValidator = (value, t) => {
  const digits = (value || '').replace(/\D/g, '')

  if (digits.length < 7 || digits.length > 8) {
    return { valid: false, error: t('DNI deve conter 7 ou 8 dígitos') }
  }

  return { valid: true, error: null }
}

export const validateCI: DocumentValidator = (value, t) => {
  const digits = (value || '').replace(/\D/g, '')

  if (digits.length < 6 || digits.length > 10) {
    return { valid: false, error: t('CI deve conter entre 6 e 10 dígitos') }
  }

  return { valid: true, error: null }
}

export const validateSSN: DocumentValidator = (value, t) => {
  const digits = (value || '').replace(/\D/g, '')

  if (digits.length !== 9) {
    return { valid: false, error: t('SSN deve conter 9 dígitos') }
  }

  if (digits === '000000000' || digits === '123456789') {
    return { valid: false, error: t('SSN inválido') }
  }

  const area = parseInt(digits.substring(0, 3))
  if (area === 0 || area === 666 || (area >= 900 && area <= 999)) {
    return { valid: false, error: t('SSN inválido') }
  }

  return { valid: true, error: null }
}

export const validateNIF: DocumentValidator = (value, t) => {
  const digits = (value || '').replace(/\D/g, '')

  if (digits.length !== 9) {
    return { valid: false, error: t('NIF deve conter 9 dígitos') }
  }

  const firstDigit = parseInt(digits[0])
  if (![1, 2, 3, 5, 6, 7, 8, 9].includes(firstDigit)) {
    return { valid: false, error: t('NIF inválido') }
  }

  let sum = 0
  for (let i = 0; i < 8; i++) {
    sum += parseInt(digits[i]) * (9 - i)
  }
  const remainder = sum % 11
  const checkDigit = remainder < 2 ? 0 : 11 - remainder

  if (checkDigit !== parseInt(digits[8])) {
    return { valid: false, error: t('NIF inválido') }
  }

  return { valid: true, error: null }
}

/**
 * NIF espanhol — NAO e o NIF portugues acima. Sao documentos diferentes com o
 * mesmo nome: o portugues sao 9 digitos com mod-11, o espanhol termina em
 * LETRA. Validar um pelo algoritmo do outro recusa todo cliente espanhol, que
 * foi o que a franquia de Valladolid encontrou (chamado 2026-0034).
 *
 *   - DNI: 8 digitos + letra             (12345678Z)
 *   - NIE: X, Y ou Z + 7 digitos + letra (X1234567L)
 *
 * A letra e o resto da divisao do numero por 23 na tabela oficial; no NIE o
 * prefixo vira digito (X->0, Y->1, Z->2).
 */
export const validateNifEs: DocumentValidator = (value, t) => {
  const normalized = (value || '').replace(/[.\-\s]/g, '').toUpperCase()

  if (!/^[XYZ]?\d{7,8}[A-Z]$/.test(normalized)) {
    return { valid: false, error: t('NIF/NIE inválido') }
  }

  let corpo = normalized.slice(0, -1)
  const letra = normalized.slice(-1)
  const prefixos: Record<string, string> = { X: '0', Y: '1', Z: '2' }

  if (prefixos[corpo[0]] !== undefined) {
    corpo = prefixos[corpo[0]] + corpo.slice(1)
  } else if (corpo.length !== 8) {
    // 7 digitos sem prefixo X/Y/Z nao e nem DNI nem NIE.
    return { valid: false, error: t('NIF/NIE inválido') }
  }

  const tabela = 'TRWAGMYFPDXBNJZSQVHLCKE'
  if (letra !== tabela[parseInt(corpo, 10) % 23]) {
    return { valid: false, error: t('NIF/NIE inválido') }
  }

  return { valid: true, error: null }
}

export const validateCURP: DocumentValidator = (value, t) => {
  const cleaned = (value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase()

  if (cleaned.length !== 18) {
    return { valid: false, error: t('CURP deve conter 18 caracteres') }
  }

  const curpRegex = /^[A-Z]{4}[0-9]{6}[A-Z]{6}[A-Z0-9]{2}$/
  if (!curpRegex.test(cleaned)) {
    return { valid: false, error: t('CURP inválido') }
  }

  return { valid: true, error: null }
}

export const validateDPI: DocumentValidator = (value, t) => {
  const digits = (value || '').replace(/\D/g, '')

  if (digits.length !== 13) {
    return { valid: false, error: t('DPI deve conter 13 dígitos') }
  }

  return { valid: true, error: null }
}

export const validateCedula: DocumentValidator = (value, t) => {
  const digits = (value || '').replace(/\D/g, '')

  if (digits.length < 6 || digits.length > 8) {
    return { valid: false, error: t('Cédula deve conter entre 6 e 8 dígitos') }
  }

  return { valid: true, error: null }
}

/**
 * Mod-10 (Módulo 10) check-digit test for an Ecuadorian cédula.
 *
 * The first 9 digits are multiplied by weights [2,1,2,1,2,1,2,1,2] from left to right;
 * products >= 10 have 9 subtracted; the results are summed and
 * check = (10 - (sum % 10)) % 10 must equal the 10th digit.
 *
 * Shared by validateCedulaEC and the RUC natural-person branch so the two stay in lockstep.
 */
const isValidCedulaCheckDigit = (digits: string): boolean => {
  const weights = [2, 1, 2, 1, 2, 1, 2, 1, 2]
  let sum = 0

  for (let i = 0; i < 9; i++) {
    let product = parseInt(digits[i]) * weights[i]
    if (product >= 10) {
      product -= 9
    }
    sum += product
  }

  const checkDigit = (10 - (sum % 10)) % 10
  return checkDigit === parseInt(digits[9])
}

/**
 * Mod-11 check digit used by the RUC public/private branches.
 *
 * The digits are multiplied by the supplied weights; sum them, m = sum % 11,
 * and check = (m === 0) ? 0 : 11 - m. A result of 10 (m === 1) has no single-digit
 * representation, so any RUC that computes to it is rejected by the caller.
 */
const mod11CheckDigit = (digits: string, weights: number[]): number => {
  let sum = 0
  for (let i = 0; i < weights.length; i++) {
    sum += parseInt(digits[i]) * weights[i]
  }
  const m = sum % 11
  return m === 0 ? 0 : 11 - m
}

/**
 * Validate Ecuadorian Cédula de Ciudadanía
 *
 * Format: exactly 10 digits.
 * - province = first 2 digits, must be 01-24.
 * - third digit identifies the person type, must be 0-5 for a natural person.
 * - Mod-10 (Módulo 10) check digit on the first 9 digits (weights [2,1,2,1,2,1,2,1,2],
 *   products >= 10 lose 9), must equal the 10th digit.
 */
export const validateCedulaEC: DocumentValidator = (value, t) => {
  const digits = (value || '').replace(/\D/g, '')

  if (digits.length !== 10) {
    return { valid: false, error: t('Cédula equatoriana deve conter 10 dígitos') }
  }

  // Validate province: 01-24 are valid Ecuador provinces
  const provinceCode = parseInt(digits.substring(0, 2))
  if (provinceCode < 1 || provinceCode > 24) {
    return { valid: false, error: t('Cédula equatoriana inválida — código de província incorreto') }
  }

  // Validate third digit: 0-5 identifies a natural person
  const thirdDigit = parseInt(digits[2])
  if (thirdDigit < 0 || thirdDigit > 5) {
    return { valid: false, error: t('Cédula equatoriana inválida — terceiro dígito incorreto') }
  }

  if (!isValidCedulaCheckDigit(digits)) {
    return { valid: false, error: t('Cédula equatoriana inválida') }
  }

  return { valid: true, error: null }
}

/**
 * Validate Ecuadorian RUC (Registro Único de Contribuyentes)
 *
 * Format: exactly 13 digits = province (01-24) + entity discriminator (3rd digit) + body + establishment.
 * The 3rd digit selects the entity type and its check-digit algorithm:
 * - 0-5  Natural person: the first 10 digits must be a valid cédula (Mod-10),
 *        and the establishment (last 3 digits) must not be '000'.
 * - 6    Public entity: Mod-11 with weights [3,2,7,6,5,4,3,2] over the first 8 digits,
 *        check digit at the 9th position, and the establishment (last 4 digits) must not be '0000'.
 * - 9    Private company: Mod-11 with weights [4,3,2,7,6,5,4,3,2] over the first 9 digits,
 *        check digit at the 10th position, and the establishment (last 3 digits) must not be '000'.
 * Any other 3rd digit is invalid.
 */
export const validateRUC: DocumentValidator = (value, t) => {
  const digits = (value || '').replace(/\D/g, '')

  if (digits.length !== 13) {
    return { valid: false, error: t('RUC deve conter 13 dígitos') }
  }

  // Validate province: 01-24 are valid Ecuador provinces
  const provinceCode = parseInt(digits.substring(0, 2))
  if (provinceCode < 1 || provinceCode > 24) {
    return { valid: false, error: t('RUC inválido — código de província incorreto') }
  }

  const thirdDigit = parseInt(digits[2])

  // Natural person: first 10 digits are a valid cédula, establishment (last 3) not '000'
  if (thirdDigit >= 0 && thirdDigit <= 5) {
    if (!isValidCedulaCheckDigit(digits.substring(0, 10))) {
      return { valid: false, error: t('RUC inválido') }
    }
    if (digits.substring(10, 13) === '000') {
      return { valid: false, error: t('RUC inválido — estabelecimento incorreto') }
    }
    return { valid: true, error: null }
  }

  // Public entity: Mod-11 weights [3,2,7,6,5,4,3,2] over first 8 digits, establishment (last 4) not '0000'
  if (thirdDigit === 6) {
    const checkDigit = mod11CheckDigit(digits, [3, 2, 7, 6, 5, 4, 3, 2])
    if (checkDigit !== parseInt(digits[8])) {
      return { valid: false, error: t('RUC inválido') }
    }
    if (digits.substring(9, 13) === '0000') {
      return { valid: false, error: t('RUC inválido — estabelecimento incorreto') }
    }
    return { valid: true, error: null }
  }

  // Private company: Mod-11 weights [4,3,2,7,6,5,4,3,2] over first 9 digits, establishment (last 3) not '000'
  if (thirdDigit === 9) {
    const checkDigit = mod11CheckDigit(digits, [4, 3, 2, 7, 6, 5, 4, 3, 2])
    if (checkDigit !== parseInt(digits[9])) {
      return { valid: false, error: t('RUC inválido') }
    }
    if (digits.substring(10, 13) === '000') {
      return { valid: false, error: t('RUC inválido — estabelecimento incorreto') }
    }
    return { valid: true, error: null }
  }

  return { valid: false, error: t('RUC inválido — tipo de contribuinte incorreto') }
}

export const validateCNPJ: DocumentValidator = (value, t) => {
  const digits = (value || '').replace(/\D/g, '')

  if (digits.length !== 14) {
    return { valid: false, error: t('CNPJ deve conter 14 dígitos') }
  }

  if (/^(\d)\1+$/.test(digits)) {
    return { valid: false, error: t('CNPJ inválido') }
  }

  let sum = 0
  let weight = 5
  for (let i = 0; i < 12; i++) {
    sum += parseInt(digits[i]) * weight
    weight = weight === 2 ? 9 : weight - 1
  }
  let remainder = sum % 11
  const checkDigit1 = remainder < 2 ? 0 : 11 - remainder

  if (checkDigit1 !== parseInt(digits[12])) {
    return { valid: false, error: t('CNPJ inválido') }
  }

  sum = 0
  weight = 6
  for (let i = 0; i < 13; i++) {
    sum += parseInt(digits[i]) * weight
    weight = weight === 2 ? 9 : weight - 1
  }
  remainder = sum % 11
  const checkDigit2 = remainder < 2 ? 0 : 11 - remainder

  if (checkDigit2 !== parseInt(digits[13])) {
    return { valid: false, error: t('CNPJ inválido') }
  }

  return { valid: true, error: null }
}
